import React, { Suspense, useState, useCallback } from 'react';
import { ChevronLeft, ArrowRight, Palette, Loader } from 'lucide-react';
import { useDesignSession } from '../../contexts/DesignSessionContext';
import { getDesignComponent } from '../../utils/designComponentRegistry';

/**
 * CustomDesignWrapper
 * 
 * A wrapper component that:
 * - Displays product info (name, price)
 * - Shows session status indicator
 * - Loads the appropriate design component dynamically
 * - Provides Back and Next navigation buttons
 * - Handles design data submission
 */
export default function CustomDesignWrapper({
    product,
    onComplete,
    onBack,
    initialDesignData = null
}) {
    const {
        sessionId,
        productPrice,
        priceCurrency,
        updateDesignData,
        designData
    } = useDesignSession();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    // Get the design component based on product settings
    const componentName = product?.customDesignPage?.pageComponent || 'PlaceholderDesigner';
    const DesignComponent = getDesignComponent(componentName);

    // Handle design data changes from the child component
    const handleDesignChange = useCallback((data) => {
        updateDesignData(data);
        setError(null);
    }, [updateDesignData]);

    // Handle Next button click
    const handleNext = async () => {
        if (!designData) {
            setError('Please complete your design before continuing.');
            return;
        }

        setIsSubmitting(true);
        setError(null);

        try {
            await onComplete(designData);
        } catch (err) {
            console.error('Error completing design:', err);
            setError('Failed to save design. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // Page title and description from product settings
    const pageTitle = product?.customDesignPage?.title || 'Design Your Product';
    const pageDescription = product?.customDesignPage?.description || '';

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            {/* Header */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        {/* Back Button */}
                        <button
                            onClick={onBack}
                            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                        >
                            <ChevronLeft className="w-5 h-5" />
                            <span className="hidden sm:inline">Back</span>
                        </button>

                        {/* Title */}
                        <div className="flex items-center gap-2">
                            <Palette className="w-5 h-5 text-teal-600" />
                            <h1 className="font-semibold text-gray-900 dark:text-white">
                                {pageTitle}
                            </h1>
                        </div>

                        {/* Session Indicator */}
                        <div className="text-xs text-gray-500 dark:text-gray-400 hidden sm:block">
                            Session: {sessionId?.substring(0, 8)}...
                        </div>
                    </div>
                </div>
            </div>

            {/* Product Info Bar */}
            <div className="bg-gradient-to-r from-teal-600 to-teal-700 text-white">
                <div className="max-w-4xl mx-auto px-4 py-3">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-teal-100">Designing for:</p>
                            <p className="font-semibold">{product?.name || 'Product'}</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm text-teal-100">Price:</p>
                            <p className="font-bold text-lg">
                                {priceCurrency} {productPrice?.toLocaleString() || 0}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Description */}
            {pageDescription && (
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                        <p className="text-sm text-blue-800 dark:text-blue-200">
                            {pageDescription}
                        </p>
                    </div>
                </div>
            )}

            {/* Design Component Area */}
            <div className="max-w-4xl mx-auto px-4 py-6">
                <Suspense
                    fallback={
                        <div className="flex items-center justify-center py-12">
                            <Loader className="w-8 h-8 text-teal-600 animate-spin" />
                            <span className="ml-3 text-gray-600 dark:text-gray-400">
                                Loading designer...
                            </span>
                        </div>
                    }
                >
                    <DesignComponent
                        product={product}
                        onDesignChange={handleDesignChange}
                        initialData={initialDesignData}
                    />
                </Suspense>
            </div>

            {/* Error Message */}
            {error && (
                <div className="max-w-4xl mx-auto px-4 pb-4">
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                    </div>
                </div>
            )}

            {/* Footer Navigation */}
            <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
                <div className="max-w-4xl mx-auto px-4 py-4">
                    <div className="flex items-center justify-between">
                        <button
                            onClick={onBack}
                            className="px-6 py-2.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                            Back
                        </button>

                        <button
                            onClick={handleNext}
                            disabled={isSubmitting || !designData}
                            className={`flex items-center gap-2 px-8 py-2.5 rounded-lg font-semibold transition-all ${isSubmitting || !designData
                                    ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed'
                                    : 'bg-teal-600 hover:bg-teal-700 text-white shadow-md hover:shadow-lg'
                                }`}
                        >
                            {isSubmitting ? (
                                <>
                                    <Loader className="w-4 h-4 animate-spin" />
                                    Saving...
                                </>
                            ) : (
                                <>
                                    Next
                                    <ArrowRight className="w-4 h-4" />
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
